import logger from './logger.js';
import { getMiddlewareAPIURL } from './constants/api_urls.js';
import { PROD_ENV, UAT_ENV } from './constants/environments.js';
/**
 * This is a function to get the current geolocation of the customer.
 *
 * @param {string} env dictates the version of the middleware API to call (dev / uat / prod)
 * @return {Country} Country - An object containing country code and country name
 *
 */
export async function fetchCountry(env = PROD_ENV) {
    // Get the right middleware URL for this environment
    const diswMiddlewareApiURL = getMiddlewareAPIURL(env);
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 10000);
    return fetch(`${diswMiddlewareApiURL}/country/locate`, {
        signal: controller.signal,
    })
        .then(response => (response.ok ? response.json() : undefined))
        .catch(error => {
        logger.log(`Error determining country: ${error}`);
        return undefined;
    });
}
/**
 * Fetches the user's country information from the ECBE Interops API.
 *
 * The function determines the base URL based on the provided environment (`env`).
 * It sends a GET request to the `/api/v1/user/country` endpoint, including an authorization token from local storage.
 * The request is aborted if it takes longer than 10 seconds.
 *
 * @param {string} [env=PROD_ENV] - The environment to use for the API endpoint (e.g., production, UAT).
 * @returns {Promise<any>} A promise that resolves to the user's country information.
 * @throws {Error} If the fetch request fails or the response is not OK.
 */
export async function fetchECBEUserCountry(env = PROD_ENV) {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 10000);
    const baseUrl = env === PROD_ENV ? 'public-apim.siemens.com/c00107-interop' : env === UAT_ENV ? 'qa-public-apim.siemens.com/c00107-interop-uat' : 'qa-public-apim.siemens.com/c00107-interop';
    logger.log(`Fetching user country from interops API at: https://${baseUrl}/api/v1/user/country`);
    const token = window?.disw?.account?.getAccount()?.idToken;
    try {
        const response = await fetch(`https://${baseUrl}/api/v1/user/country`, {
            headers: {
                Authorization: token ? `Bearer ${token}` : '',
            },
            mode: 'cors',
            signal: controller.signal,
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch user country through ECBE: ${response.status} - ${response.statusText}`);
        }
        return await response.json();
    }
    catch (error) {
        logger.log(`Error fetching user country: ${error}`);
        throw error;
    }
}
/**
 * Fetch the list of countries in Contentful by locale (i.e., 'en-US') and environment (corresponding middleware API).
 *
 * @param {string} localeCode representing the locale for the site (i.e., 'ja-JP')
 * @param {string} env dictates the version of the middleware API to call (dev / uat / prod)
 * @returns JSON with a countries property with keys 'key' & 'value' corresponding to the country name / code respectively.
 *
 *     fetchCountries('en-US', 'env')
 */
export async function fetchCountries(localeCode, env) {
    // Get the right middleware URL for this environment
    const diswMiddlewareApiURL = getMiddlewareAPIURL(env);
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 10000);
    return fetch(`${diswMiddlewareApiURL}/country/${localeCode}`, {
        signal: controller.signal,
    })
        .then(response => {
        if (response.ok) {
            return response.json();
        }
        console.error(`Error loading country list. API returned - '${response.status}' - with response text: '${response.statusText}'`);
        throw Error(response.statusText);
    })
        .catch(error => {
        // Error in fetch...throwing
        throw error;
    });
}
